// -----------------------------------------------------------
// FloraIQ - PlanningStagePanel (Aba Etapa 1 - Planejamento)
// -----------------------------------------------------------

import { useState } from "react";
import { planningChecklist, planningPendencias, equipmentDefaults } from "@/mocks/operational";

export default function PlanningStagePanel() {
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>(
    Object.fromEntries(planningChecklist.map((c) => [c.id, c.concluido]))
  );

  const toggleCheck = (id: string) => {
    setCheckedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const completedCount = Object.values(checkedItems).filter(Boolean).length;
  const totalCount = planningChecklist.length;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Checklist col */}
        <div className="lg:col-span-2 space-y-5">
          {/* Info card */}
          <div className="rounded-card border border-background-200/70 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-foreground-900">Checklist de Planejamento</h4>
              <span className="text-xs text-foreground-500">{completedCount}/{totalCount} concluídos</span>
            </div>
            <div className="w-full bg-background-100 rounded-full h-1.5 mb-3">
              <div className="bg-primary-500 h-1.5 rounded-full transition-all duration-500" style={{ width: `${(completedCount / totalCount) * 100}%` }}></div>
            </div>
            <div className="space-y-1">
              {planningChecklist.map((item) => (
                <button
                  key={item.id}
                  onClick={() => toggleCheck(item.id)}
                  className="w-full flex items-start gap-2.5 p-2.5 rounded-lg text-left transition-colors cursor-pointer hover:bg-background-50/70"
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-all ${
                    checkedItems[item.id]
                      ? "bg-green-500 border-green-500 text-white"
                      : "border-background-300 text-transparent hover:border-primary-300"
                  }`}>
                    <i className="ri-check-line text-xs"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs ${checkedItems[item.id] ? "text-foreground-500 line-through" : "text-foreground-800"}`}>
                      {item.descricao}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-foreground-400">{item.responsavel}</span>
                      <span className="text-[10px] text-foreground-300">•</span>
                      <span className="text-[10px] text-foreground-400">{item.prazo}</span>
                    </div>
                    {item.observacao && (
                      <p className="text-[10px] text-foreground-400 mt-0.5 italic">{item.observacao}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Pendências */}
          <div className="rounded-card border border-background-200/70 p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-orange-100 flex items-center justify-center">
                <i className="ri-alert-line text-orange-600 text-sm"></i>
              </div>
              <h4 className="text-sm font-bold text-foreground-900">Pendências</h4>
              <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full font-medium">{planningPendencias.length}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200/50">
                    <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Item</th>
                    <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Responsável</th>
                    <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Prazo</th>
                    <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Prioridade</th>
                    <th className="text-left px-2 py-2 text-[10px] font-medium text-foreground-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {planningPendencias.map((p) => (
                    <tr key={p.id} className="border-b border-background-100/70">
                      <td className="px-2 py-2 text-xs text-foreground-700">{p.item}</td>
                      <td className="px-2 py-2 text-xs text-foreground-600">{p.responsavel}</td>
                      <td className="px-2 py-2 text-xs text-foreground-600">{p.prazo}</td>
                      <td className="px-2 py-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${
                          p.prioridade === "alta" ? "bg-red-100 text-red-700" : p.prioridade === "media" ? "bg-orange-100 text-orange-700" : "bg-yellow-100 text-yellow-700"
                        }`}>{p.prioridade}</span>
                      </td>
                      <td className="px-2 py-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${
                          p.status === "resolvida" ? "bg-green-100 text-green-700" : p.status === "em_andamento" ? "bg-blue-100 text-blue-700" : "bg-orange-100 text-orange-700"
                        }`}>{p.status === "resolvida" ? "OK" : p.status === "em_andamento" ? "Em andamento" : "Pendente"}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Side col */}
        <div className="space-y-5">
          {/* Dados da Etapa */}
          <div className="rounded-card border border-background-200/70 p-4">
            <h4 className="text-sm font-bold text-foreground-900 mb-3">Dados da Etapa</h4>
            <div className="space-y-2 text-xs">
              {[
                { label: "Área de Estudo", value: "Setor Central - 4,8 km²" },
                { label: "Município", value: "Goiânia / GO" },
                { label: "Bairros/Setores", value: "Setor Central, Setor Sul, Setor Oeste" },
                { label: "Responsável Técnico", value: "Dr. Roberto Almeida" },
                { label: "Equipe Prevista", value: "6 técnicos de campo + 1 coord." },
                { label: "Data de Início", value: "01/03/2026" },
                { label: "Data Prevista Campo", value: "15/04/2026" },
                { label: "Metodologia", value: "FloraIQ-LADM (Completa)" },
              ].map((d) => (
                <div key={d.label} className="flex justify-between py-1 border-b border-background-100/70 last:border-0">
                  <span className="text-foreground-500">{d.label}</span>
                  <span className="text-foreground-800 font-medium text-right ml-2">{d.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Equipamentos */}
          <div className="rounded-card border border-background-200/70 p-4">
            <h4 className="text-sm font-bold text-foreground-900 mb-3">Equipamentos Previstos</h4>
            <div className="space-y-1.5">
              {equipmentDefaults.map((eq) => (
                <div key={eq.nome} className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${eq.disponivel ? "bg-green-500" : "bg-red-500"}`}></div>
                    <span className="text-xs text-foreground-700">{eq.nome}</span>
                  </div>
                  <span className="text-[10px] text-foreground-400">x{eq.quantidade}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}